import {
  Engine,
  Scene,
  FreeCamera,
  Vector3,
  Color3,
  ActionManager,
  ActionEvent,
  PickingInfo,
  GlowLayer,
  DefaultRenderingPipeline,
  SSAO2RenderingPipeline,
  ImageProcessingConfiguration,
} from "@babylonjs/core";
import { CharacterController } from "../player/CharacterController.js";
import { isTouchDevice } from "../util/device.js";
import { RemotePlayer } from "../player/RemotePlayer.js";
import { NpcRenderer } from "./NpcRenderer.js";
import { lookStickDelta } from "../input/lookStickMath.js";
import type { LookDelta } from "../input/lookStickMath.js";

/**
 * Thin wrapper around Babylon Engine + Scene + camera. Static world geometry
 * is built by Zones (world/zones/); movement is owned by CharacterController
 * (grounded walk, sprint, jump — the camera itself never flies). This class
 * wires input sources (keyboard, touch joystick) into the controller and
 * manages remote avatars.
 */
export class MallScene {
  engine: Engine;
  scene: Scene;
  camera: FreeCamera;
  controller: CharacterController;
  private remoteAvatars: Map<string, RemotePlayer> = new Map();
  /** Server-simulated shoppers; see NpcRenderer. */
  readonly npcs: NpcRenderer;
  private keys: { forward: boolean; back: boolean; left: boolean; right: boolean; sprint: boolean } =
    { forward: false, back: false, left: false, right: false, sprint: false };
  private joystick: { x: number; y: number } = { x: 0, y: 0 };
  private lookStick: { x: number; y: number } = { x: 0, y: 0 };

  constructor(canvas: HTMLCanvasElement) {
    this.engine = new Engine(canvas, true, { preserveDrawingBuffer: true, stencil: true });
    // Without this the engine renders at CSS resolution: on a 3x phone that
    // is 1/9th the pixels the screen has, and every texture reads as mush.
    // Render at real device pixels, capped at 2x so high-DPR phones don't
    // pay a 9x fill-rate bill; the mobile tier below sheds SSAO to keep the
    // frame budget.
    this.engine.setHardwareScalingLevel(1 / Math.min(window.devicePixelRatio, 2));
    this.scene = new Scene(this.engine);
    this.scene.clearColor.set(0.04, 0.045, 0.05, 1.0);
    this.scene.ambientColor = new Color3(0.33, 0.35, 0.37);
    this.scene.collisionsEnabled = true;

    // Neon: everything emissive (signs, accent tubes, holograms) blooms.
    // Named "mall-glow" so surfaces can find it and exclude themselves.
    const glow: GlowLayer = new GlowLayer("mall-glow", this.scene, { blurKernelSize: 32 });
    glow.intensity = 0.65;

    this.camera = new FreeCamera("localCamera", new Vector3(0, 1.7, 0), this.scene);
    this.camera.setTarget(new Vector3(0, 1.7, 1));
    this.camera.attachControl(canvas, true);
    // Higher = slower mouse look (Babylon's sensibility is inverse). 3500
    // felt twitchy under pointer lock; 6000 is a calmer default.
    this.camera.angularSensibility = 6000;
    this.camera.minZ = 0.1;

    // Movement is fully controller-driven: strip Babylon's built-in keyboard
    // and touch camera inputs (mouse-look stays attached).
    for (const name of ["keyboard", "touch"]) {
      const input = this.camera.inputs.attached[name];
      if (input !== undefined) {
        this.camera.inputs.remove(input);
      }
    }

    this.npcs = new NpcRenderer(this.scene);
    this.controller = new CharacterController(this.scene, this.camera);
    this.installKeyboard();
    this.installPointerLock(canvas);
    this.installPostProcessing();
    // Dev handle for camera/controller scripting (screenshot iteration).
    (window as unknown as { __mm: MallScene }).__mm = this;

    this.scene.onBeforeRenderObservable.add(() => {
      const dt: number = this.engine.getDeltaTime() / 1000;
      this.controller.setInput({
        axisX: this.keyboardAxisX() + this.joystick.x,
        axisY: this.keyboardAxisY() + this.joystick.y,
        sprint: this.keys.sprint,
      });
      this.controller.update(dt);
      const look: LookDelta = lookStickDelta(this.lookStick.x, this.lookStick.y, dt);
      if (look.dyaw !== 0 || look.dpitch !== 0) {
        this.camera.rotation.y += look.dyaw;
        this.camera.rotation.x = Math.max(
          -Math.PI / 2 + 0.05,
          Math.min(Math.PI / 2 - 0.05, this.camera.rotation.x + look.dpitch)
        );
      }
    });

    window.addEventListener("resize", () => this.engine.resize());
  }

  /** Filmic look: ACES tone mapping stops the white walls from clipping,
   *  FXAA smooths the box-built geometry, soft bloom sells the LED strips,
   *  and SSAO grounds fixtures with contact shadows. */
  private installPostProcessing(): void {
    const pipeline: DefaultRenderingPipeline = new DefaultRenderingPipeline(
      "mall-pipeline",
      true,
      this.scene,
      [this.camera]
    );
    pipeline.fxaaEnabled = true;
    pipeline.imageProcessingEnabled = true;
    pipeline.imageProcessing.toneMappingEnabled = true;
    pipeline.imageProcessing.toneMappingType = ImageProcessingConfiguration.TONEMAPPING_ACES;
    // Retuned for the PBR/IBL lighting rig: the old 0.85 exposure was
    // compensating for stacked emissive lifts that no longer exist.
    pipeline.imageProcessing.exposure = 0.95;
    pipeline.imageProcessing.contrast = 1.1;
    pipeline.bloomEnabled = true;
    // High threshold: only the over-unity LED strips bloom, never the white
    // architecture (a lower threshold fogs the whole bright hall).
    pipeline.bloomThreshold = 0.92;
    pipeline.bloomWeight = 0.2;
    pipeline.bloomKernel = 48;

    // SSAO2 needs WebGL2; skip silently on older devices. Also skipped on
    // touch devices: at full device-pixel resolution SSAO's ratio passes are
    // the single most expensive effect, and phones need the frame budget.
    if (this.engine.webGLVersion >= 2 && !isTouchDevice()) {
      const ssao: SSAO2RenderingPipeline = new SSAO2RenderingPipeline(
        "mall-ssao",
        this.scene,
        { ssaoRatio: 0.5, blurRatio: 0.5 },
        [this.camera]
      );
      ssao.radius = 1.6;
      ssao.totalStrength = 1.1;
      ssao.samples = 12;
    }
  }

  /** Resolve when the scene AND all its textures are actually renderable, so
   *  the loading screen holds until nothing pops in late. Progress is
   *  reported as (ready, total) texture counts. No flat time cap: players
   *  wait for a full load. The only escape is a stall detector (no texture
   *  progress for 20s straight), so a single dead texture URL can't lock
   *  everyone out of the mall forever. */
  async whenWorldReady(onProgress?: (ready: number, total: number) => void): Promise<void> {
    await this.scene.whenReadyAsync();
    let lastReady: number = -1;
    let lastProgressAt: number = performance.now();
    for (;;) {
      const textures = this.scene.textures;
      const ready: number = textures.filter((t) => t.isReady()).length;
      if (onProgress !== undefined) {
        onProgress(ready, textures.length);
      }
      if (ready >= textures.length) {
        return;
      }
      if (ready !== lastReady) {
        lastReady = ready;
        lastProgressAt = performance.now();
      } else if (performance.now() - lastProgressAt > 20_000) {
        console.warn(
          `[Mall Mart] Texture loading stalled at ${ready}/${textures.length}; entering anyway`
        );
        return;
      }
      await new Promise((r) => setTimeout(r, 150));
    }
  }

  /** Accept analog input from a touch joystick. x right-positive, y forward-positive. */
  setJoystick(x: number, y: number): void {
    this.joystick.x = x;
    this.joystick.y = y;
  }

  /** Accept analog input from the right-hand look stick. x right-positive, y up-positive. */
  setLookStick(x: number, y: number): void {
    this.lookStick.x = x;
    this.lookStick.y = y;
  }

  jump(): void {
    this.controller.setInput({ jump: true });
  }

  private keyboardAxisX(): number {
    return (this.keys.right ? 1 : 0) - (this.keys.left ? 1 : 0);
  }

  private keyboardAxisY(): number {
    return (this.keys.forward ? 1 : 0) - (this.keys.back ? 1 : 0);
  }

  /** True when the key event comes from a form control (panel inputs),
   *  where movement keys must not fire. The canvas and body both count as
   *  game surfaces: the canvas takes focus after a click, and the old
   *  target===body guard silently killed WASD/arrows after clicking it. */
  private static isTypingTarget(e: KeyboardEvent): boolean {
    const tag: string = (e.target as HTMLElement | null)?.tagName ?? "";
    return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || tag === "BUTTON";
  }

  private installKeyboard(): void {
    const apply = (code: string, pressed: boolean): void => {
      if (code === "KeyW" || code === "ArrowUp") this.keys.forward = pressed;
      if (code === "KeyS" || code === "ArrowDown") this.keys.back = pressed;
      if (code === "KeyA" || code === "ArrowLeft") this.keys.left = pressed;
      if (code === "KeyD" || code === "ArrowRight") this.keys.right = pressed;
    };
    window.addEventListener("keydown", (e: KeyboardEvent) => {
      if (MallScene.isTypingTarget(e)) {
        return; // typing in a panel input
      }
      if (e.code.startsWith("Arrow") || e.code === "Space") {
        e.preventDefault(); // no page scroll / button re-trigger
      }
      apply(e.code, true);
      if (e.code === "Space") {
        this.controller.setInput({ jump: true });
      }
    });
    window.addEventListener("keyup", (e: KeyboardEvent) => {
      apply(e.code, false);
    });
    window.addEventListener("blur", () => {
      this.keys.forward = this.keys.back = this.keys.left = this.keys.right = false;
      this.keys.sprint = false;
    });
  }

  /** Desktop mouse look: first click on the canvas grabs the pointer
   *  (mouse then steers the camera, Babylon's mouse input reads movementX/Y
   *  under pointer lock); while locked, clicks pick whatever is under the
   *  center crosshair. Esc releases the pointer as usual. Touch-only devices
   *  keep the drag-look path and never request the lock. */
  private installPointerLock(canvas: HTMLCanvasElement): void {
    if (!window.matchMedia("(pointer: fine)").matches) {
      return;
    }
    const crosshair: HTMLElement | null = document.getElementById("crosshair");
    canvas.addEventListener("click", () => {
      if (!this.engine.isPointerLock) {
        // Never grab the pointer while a UI panel is open: the user needs
        // the cursor there, and clicking a product must not re-lock.
        if (!this.uiOpen()) {
          this.engine.enterPointerlock();
        }
        return;
      }
      const pick: PickingInfo | null = this.scene.pick(
        this.engine.getRenderWidth() / 2,
        this.engine.getRenderHeight() / 2
      );
      const mesh = pick?.pickedMesh;
      if (mesh !== null && mesh !== undefined && mesh.actionManager !== null) {
        mesh.actionManager.processTrigger(
          ActionManager.OnPickTrigger,
          ActionEvent.CreateNew(mesh)
        );
      }
    });
    document.addEventListener("pointerlockchange", () => {
      if (crosshair !== null) {
        crosshair.style.display = document.pointerLockElement !== null ? "block" : "none";
      }
    });
  }

  private uiOpen: () => boolean = () => false;

  /** Register a predicate for "a UI panel is open" so pointer lock is never
   *  (re)entered underneath an open panel. */
  setUiOpenCheck(fn: () => boolean): void {
    this.uiOpen = fn;
  }

  /** Release desktop pointer lock (called when a UI panel opens). */
  releasePointerLock(): void {
    if (this.engine.isPointerLock) {
      this.engine.exitPointerlock();
    }
  }

  start(onTick?: () => void): void {
    this.engine.runRenderLoop(() => {
      if (onTick !== undefined) {
        onTick();
      }
      this.scene.render();
    });
  }

  addRemotePlayer(sessionId: string, x: number, y: number, z: number): void {
    if (this.remoteAvatars.has(sessionId)) {
      return;
    }
    this.remoteAvatars.set(sessionId, new RemotePlayer(this.scene, sessionId, x, y, z));
  }

  updateRemotePlayer(sessionId: string, x: number, y: number, z: number, rotation: number): void {
    this.remoteAvatars.get(sessionId)?.setTransform(x, y, z, rotation);
  }

  removeRemotePlayer(sessionId: string): void {
    const player: RemotePlayer | undefined = this.remoteAvatars.get(sessionId);
    if (player === undefined) {
      return;
    }
    player.dispose();
    this.remoteAvatars.delete(sessionId);
  }
}
